import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { CheckCircle, ArrowLeft, Home } from 'lucide-react';
import subscriptionApi from '../../services/subscriptionApi';
import * as Sentry from "@sentry/react";

const SubscriptionSuccess = () => {
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkSubscriptionStatus = async () => {
            try {
                const queryParams = new URLSearchParams(location.search);
                const sessionId = queryParams.get('session_id');

                if (!sessionId) {
                    setError("No session ID provided. Cannot verify subscription status.");
                    setLoading(false);
                    return;
                }

                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                if (!userData?.token) {
                    navigate('/login', { replace: true });
                    return;
                }

                // Verify subscription using session ID
                const verificationResult = await subscriptionApi.verifySubscriptionSession(sessionId);
                
                if (verificationResult.success) {
                    setSubscription(verificationResult.subscription);
                } else {
                    setError(verificationResult.message);
                }
                
            } catch (error) {
                Sentry.captureException(error);
                setError(error.message || 'An unexpected error occurred while verifying your subscription');
                console.error('Subscription verification error:', error);
            } finally {
                setLoading(false);
            }
        };

        // Start verification process
        checkSubscriptionStatus();
    }, [location.search, navigate]);

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount / 100);
    };

    const formatStatus = (status) => {
        if (!status) return '';
        // Remove enum prefix if present (e.g., "SubscriptionStatus.active" -> "active")
        const cleanStatus = status.includes('.') ? status.split('.').pop() : status;
        return cleanStatus.charAt(0).toUpperCase() + cleanStatus.slice(1);
    };

    const renderLoadingState = () => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
                Verifying your subscription...
            </Typography>
            <Typography variant="body1" color="text.secondary">
                This may take a few moments. Please don't close this page.
            </Typography>
        </Box>
    );

    const renderErrorState = () => (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" color="error" gutterBottom>
                Verification Failed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {error}
            </Typography>
            <Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/billing')}
                    startIcon={<ArrowLeft size={18} />}
                >
                    Back to Billing
                </Button>
            </Box>
        </Box>
    );

    const renderSuccessState = () => (
        <Box sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{ mb: 4 }}>
                <CheckCircle size={80} color="#4caf50" />
            </Box>

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
                Subscription Activated!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                Your subscription has been successfully activated. You can now enjoy all the benefits of your plan.
            </Typography>

            {subscription && (
                <Card sx={{ maxWidth: 500, mx: 'auto', mb: 4, textAlign: 'left' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            Subscription Details
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Plan:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {subscription.plan?.name}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Monthly Credits:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {subscription.plan?.monthly_credits?.toLocaleString()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Monthly Cost:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {formatCurrency(subscription.plan?.price_cents)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="text.secondary">Next Billing:</Typography>
                            <Typography variant="body1" fontWeight="500">
                                {new Date(subscription.current_period_end).toLocaleDateString()}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Status:</Typography>
                            <Typography
                                variant="body1"
                                fontWeight="500"
                                sx={{
                                    color: subscription.status?.includes('active') ? 'success.main' : 'warning.main'
                                }}
                            >
                                {formatStatus(subscription.status)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Home size={18} />}
                    onClick={() => navigate('/')}
                    sx={{ mr: 2 }}
                >
                    Go to Dashboard
                </Button>

                <Button
                    variant="outlined"
                    startIcon={<ArrowLeft size={18} />}
                    onClick={() => navigate('/billing')}
                >
                    Back to Billing
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%', mx: 2 }}>
                    {loading && renderLoadingState()}
                    {error && renderErrorState()}
                    {!loading && !error && renderSuccessState()}
                </Paper>
            </Box>
        </Box>
    );
};

export default SubscriptionSuccess; 